import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";

export interface Company {
  id: string;
  name: string;
  legal_name: string | null;
  logo_url: string | null;
  base_currency: string;
  country: string;
  timezone: string;
  vat_registration_number: string | null;
  fiscal_year_start_month: number;
  workspace_id: string;
  is_demo?: boolean;
  deleted_at?: string | null;
}

export interface CompanyMembership extends Company {
  role: "owner" | "admin" | "accountant" | "reviewer" | "viewer";
}

export interface Workspace {
  id: string;
  name: string;
  role: "owner" | "admin" | "member";
  primaryCompanyName?: string | null;
}

interface CompanyContextValue {
  companies: CompanyMembership[];
  currentCompany: CompanyMembership | null;
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  loading: boolean;
  selectCompany: (id: string) => Promise<void>;
  selectWorkspace: (id: string) => Promise<void>;
  refresh: (opts?: { silent?: boolean }) => Promise<void>;
}

const CompanyContext = createContext<CompanyContextValue | undefined>(undefined);
const STORAGE_KEY = "sayda_current_company_id";
const WS_STORAGE_KEY = "sayda_current_workspace_id";

// Value comparisons used to avoid replacing state with an equal-but-new object.
// Reference churn is what remounts pages and makes the app look like it reloads.
export const sameWorkspace = (a: Workspace, b: Workspace) =>
  a.id === b.id && a.name === b.name && a.role === b.role &&
  (a.primaryCompanyName ?? null) === (b.primaryCompanyName ?? null);

export const sameWorkspaceList = (a: Workspace[], b: Workspace[]) =>
  a.length === b.length && a.every((w, i) => sameWorkspace(w, b[i]));

export const sameCompany = (a: CompanyMembership, b: CompanyMembership) =>
  a.id === b.id && a.role === b.role && a.name === b.name &&
  a.workspace_id === b.workspace_id && (a.deleted_at ?? null) === (b.deleted_at ?? null) &&
  a.base_currency === b.base_currency && (a.logo_url ?? null) === (b.logo_url ?? null) &&
  (a.is_demo ?? false) === (b.is_demo ?? false) && a.timezone === b.timezone &&
  !!(a as any).is_primary_for_workspace === !!(b as any).is_primary_for_workspace;

export const sameCompanyList = (a: CompanyMembership[], b: CompanyMembership[]) =>
  a.length === b.length && a.every((c, i) => sameCompany(c, b[i]));


export const CompanyProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<CompanyMembership[]>([]);
  const [currentCompany, setCurrentCompany] = useState<CompanyMembership | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);

  // Single-flight + ordering guards. Several triggers (mount, tab focus,
  // realtime membership changes, explicit workspace switch) can all ask for a
  // refresh at once. Without these guards two overlapping runs each recompute
  // the workspace/company selection from scratch and whichever finishes LAST
  // wins, so an account that belongs to several workspaces (some expired, some
  // without companies) can silently flip to another workspace — which looks
  // exactly like the app reloading itself.
  const runSeq = useRef(0);
  const inFlight = useRef<Promise<void> | null>(null);
  const currentWsIdRef = useRef<string | null>(null);
  const currentCompanyIdRef = useRef<string | null>(null);

  const runRefresh = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    const myRun = ++runSeq.current;
    // Only the most recently started run may commit state.
    const isStale = () => myRun !== runSeq.current;
    if (!user) {
      setCompanies([]);
      setCurrentCompany(null);
      setWorkspaces([]);
      setCurrentWorkspace(null);
      currentWsIdRef.current = null;
      currentCompanyIdRef.current = null;
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);

    // Load workspaces the user belongs to
    const { data: wsRows } = await (supabase as any)
      .from("workspace_members")
      .select("role, workspace_id, workspace_subscriptions:workspace_id(id, name, status)")
      .eq("user_id", user.id);

    const ws: Workspace[] = (wsRows || [])
      .filter((r: any) => r.workspace_subscriptions)
      .map((r: any) => ({
        id: r.workspace_subscriptions.id,
        name: r.workspace_subscriptions.name || "Workspace",
        role: r.role,
      }));
    const wsStatus = new Map<string, string | null>(
      (wsRows || [])
        .filter((r: any) => r.workspace_subscriptions)
        .map((r: any) => [r.workspace_subscriptions.id, r.workspace_subscriptions.status ?? null]),
    );
    if (isStale()) return;

    // Load the user's company memberships BEFORE picking a workspace, so the
    // chosen workspace is never one where the user has no company at all.
    const { data: cmRows, error } = await supabase
      .from("company_members")
      .select("role, companies(*)")
      .eq("user_id", user.id);
    if (isStale()) return;
    if (error) {
      console.error(error);
      setLoading(false);
      return;
    }

    const allMemberships: CompanyMembership[] = (cmRows || [])
      .filter((m: any) => m.companies)
      .map((m: any) => ({ ...(m.companies as Company), role: m.role }));

    const companyCountByWs = new Map<string, number>();
    for (const c of allMemberships) {
      if (c.deleted_at) continue;
      companyCountByWs.set(c.workspace_id, (companyCountByWs.get(c.workspace_id) || 0) + 1);
    }
    const hasCompanies = (id: string | undefined | null) => !!id && (companyCountByWs.get(id) || 0) > 0;

    // Determine current workspace. The workspace already open in this session
    // always wins while it stays valid, so a background refresh can never move
    // the user to a different workspace behind their back.
    let chosenWs: Workspace | null = null;
    const activeWsId = currentWsIdRef.current;
    if (activeWsId) {
      const active = ws.find((w) => w.id === activeWsId) || null;
      if (active && hasCompanies(active.id)) chosenWs = active;
    }
    if (!chosenWs) {
      const storedWs = localStorage.getItem(WS_STORAGE_KEY);
      if (storedWs) chosenWs = ws.find((w) => w.id === storedWs) || null;
    }
    if (!chosenWs || !hasCompanies(chosenWs.id)) {
      const { data: prof } = await supabase.from("profiles").select("current_workspace_id").eq("id", user.id).maybeSingle();
      if (isStale()) return;
      const profWsId = (prof as any)?.current_workspace_id;
      const profWs = profWsId ? ws.find((w) => w.id === profWsId) || null : null;
      if (profWs && hasCompanies(profWs.id)) chosenWs = profWs;
      else if (!chosenWs) chosenWs = profWs;
    }
    if (!chosenWs || !hasCompanies(chosenWs.id)) {
      // Deterministic fallback: active/trialing workspaces with companies first,
      // then any workspace with companies, ordered by id so two runs that see
      // the same data always land on the same workspace.
      const withCompanies = ws.filter((w) => hasCompanies(w.id)).slice().sort((a, b) => a.id.localeCompare(b.id));
      const preferred =
        withCompanies.find((w) => (wsStatus.get(w.id) ?? "") !== "expired") || withCompanies[0] || null;
      if (preferred) chosenWs = preferred;
    }
    if (!chosenWs) chosenWs = ws.slice().sort((a, b) => a.id.localeCompare(b.id))[0] || null;
    if (chosenWs) {
      localStorage.setItem(WS_STORAGE_KEY, chosenWs.id);
    } else {
      localStorage.removeItem(WS_STORAGE_KEY);
    }




    // Attach primary company name to each workspace
    const primaryByWs = new Map<string, string>();
    for (const c of allMemberships) {
      if ((c as any).is_primary_for_workspace) primaryByWs.set(c.workspace_id, c.name);
    }
    const missing = ws.filter((w) => !primaryByWs.has(w.id)).map((w) => w.id);
    if (missing.length > 0) {
      const { data: primaries } = await (supabase as any)
        .from("companies")
        .select("name, workspace_id")
        .in("workspace_id", missing)
        .eq("is_primary_for_workspace", true);
      if (isStale()) return;
      for (const p of primaries || []) primaryByWs.set(p.workspace_id, p.name);
    }
    if (isStale()) return;
    const wsWithPrimary = ws.map((w) => ({ ...w, primaryCompanyName: primaryByWs.get(w.id) || null }));
    setWorkspaces((prev) => (sameWorkspaceList(prev, wsWithPrimary) ? prev : wsWithPrimary));
    if (chosenWs) chosenWs = wsWithPrimary.find((w) => w.id === chosenWs!.id) || chosenWs;

    const memberships = chosenWs
      ? allMemberships.filter((c) => c.workspace_id === chosenWs!.id)
      : allMemberships;

    // A background refresh must never empty an already working session: an
    // empty result there means a transient read, not a lost membership. The
    // app-level "you have no company" redirect only reacts to the visible
    // (non-silent) load.
    if (silent && memberships.length === 0 && currentCompanyIdRef.current) {
      setLoading(false);
      return;
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    const keptId = currentCompanyIdRef.current;
    const found =
      (keptId ? memberships.find((c) => c.id === keptId) : null) ||
      memberships.find((c) => c.id === stored) ||
      memberships[0] ||
      null;



    // Commit selections only when the identity actually changed, so a
    // background refresh never replaces object references and never remounts
    // the pages that depend on them.
    const nextWsId = chosenWs?.id ?? null;
    if (nextWsId !== currentWsIdRef.current) {
      currentWsIdRef.current = nextWsId;
      setCurrentWorkspace(chosenWs);
    } else if (chosenWs) {
      setCurrentWorkspace((prev) => (prev && sameWorkspace(prev, chosenWs!) ? prev : chosenWs));
    }

    setCompanies((prev) => (sameCompanyList(prev, memberships) ? prev : memberships));

    const nextCompanyId = found?.id ?? null;
    if (nextCompanyId !== currentCompanyIdRef.current) {
      currentCompanyIdRef.current = nextCompanyId;
      setCurrentCompany(found);
    } else if (found) {
      setCurrentCompany((prev) => (prev && sameCompany(prev, found) ? prev : found));
    }
    if (found) localStorage.setItem(STORAGE_KEY, found.id);
    setLoading(false);
  }, [user?.id]);

  // Single-flight: while a refresh is running, every other request joins it
  // instead of starting a competing run that could resolve out of order.
  const refresh = useCallback((opts?: { silent?: boolean }): Promise<void> => {
    if (inFlight.current) return inFlight.current;
    const run = (async () => {
      try {
        await runRefresh(opts);
      } finally {
        inFlight.current = null;
      }
    })();
    inFlight.current = run;
    return run;
  }, [runRefresh]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh memberships: realtime membership changes + on tab focus.
  // Silent (no loading flicker) and never resets the current selection.
  useEffect(() => {
    if (!user) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastRunAt = Date.now();
    // Membership changes are rare, while focus/visibility can fire many times a
    // minute (notably inside an embedded preview). Coalesce bursts and keep a
    // floor between background reads.
    const MIN_GAP_MS = 60_000;
    const scheduleSilentRefresh = (opts?: { immediate?: boolean }) => {
      if (timer) clearTimeout(timer);
      const wait = opts?.immediate ? 600 : Math.max(600, MIN_GAP_MS - (Date.now() - lastRunAt));
      timer = setTimeout(() => {
        lastRunAt = Date.now();
        refresh({ silent: true });
      }, wait);
    };
    const onMembershipChange = () => scheduleSilentRefresh({ immediate: true });

    const channel = supabase
      .channel(`membership-sync-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "company_members", filter: `user_id=eq.${user.id}` }, onMembershipChange)
      .on("postgres_changes", { event: "*", schema: "public", table: "workspace_members", filter: `user_id=eq.${user.id}` }, onMembershipChange)
      .subscribe();

    const onVisible = () => { if (document.visibilityState === "visible") scheduleSilentRefresh(); };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      supabase.removeChannel(channel);
    };
  }, [user?.id, refresh]);


  const selectCompany = async (id: string) => {
    const found = companies.find((c) => c.id === id);
    if (found) {
      currentCompanyIdRef.current = found.id;
      setCurrentCompany(found);
      localStorage.setItem(STORAGE_KEY, id);
    }
  };

  const selectWorkspace = async (id: string) => {
    const found = workspaces.find((w) => w.id === id);
    if (!found || !user) return;
    // An explicit user choice outranks any refresh already in flight: mark that
    // run stale and record the new selection before reloading memberships.
    runSeq.current += 1;
    inFlight.current = null;
    currentWsIdRef.current = id;
    currentCompanyIdRef.current = null;
    setCurrentWorkspace(found);
    localStorage.setItem(WS_STORAGE_KEY, id);
    // Persist on profile (best effort)
    await supabase.from("profiles").update({ current_workspace_id: id } as any).eq("id", user.id);
    // Reset current company selection so refresh picks first company in new workspace
    localStorage.removeItem(STORAGE_KEY);
    await refresh();
  };

  return (
    <CompanyContext.Provider value={{
      companies, currentCompany, workspaces, currentWorkspace,
      loading, selectCompany, selectWorkspace, refresh,
    }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used within CompanyProvider");
  return ctx;
};
